import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Grid,
  Paper,
  useTheme,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  CheckCircleOutline as CheckIcon,
  CompareArrows as CompareIcon,
  Timer as TimerIcon,
  ErrorOutline as ErrorIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { Evaluation } from "../../types";
import { useNavigate } from "react-router-dom";

interface EvaluationResultsModalProps {
  open: boolean;
  onClose: () => void;
  evaluation: Evaluation;
}

const EvaluationResultsModal: React.FC<EvaluationResultsModalProps> = ({
  open,
  onClose,
  evaluation,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  if (!evaluation || !evaluation.results) {
    return null;
  }

  // Extract model data from results
  const getModelResults = () => {
    if (!evaluation.results || !evaluation.results.results) {
      return [];
    }

    const results = evaluation.results.results as any[];
    const providerResults = new Map<
      string,
      { score: number; latency: number; tokenUsage: number; cost: number }
    >();

    results.forEach((result) => {
      // Safely extract provider name ensuring it's a string
      const providerId = result.provider?.id || "unknown";
      const provider =
        evaluation.providers?.find((p) => p._id === providerId)?.name ||
        providerId;

      if (!providerResults.has(provider)) {
        providerResults.set(provider, {
          score: 0,
          latency: 0,
          tokenUsage: 0,
          cost: 0,
        });
      }

      const currentData = providerResults.get(provider)!;
      providerResults.set(provider, {
        score: currentData.score + (result.score || 0),
        latency: currentData.latency + (result.latencyMs || 0),
        tokenUsage:
          currentData.tokenUsage + (result.response?.tokenUsage?.total || 0),
        cost: currentData.cost + (result.cost || 0),
      });
    });

    // Calculate averages
    const providers = evaluation.providers || [];
    return Array.from(providerResults.entries()).map(([providerId, data]) => {
      const provider = providers.find(
        (p) => p._id === providerId || p.name === providerId,
      );
      const totalTests = results.filter((r) => {
        const provId = r.provider?.id || "";
        return (
          provId === providerId ||
          providers.find((p) => p._id === provId)?.name === providerId
        );
      }).length;

      return {
        id: providerId,
        name: provider?.name || providerId,
        score: totalTests > 0 ? data.score / totalTests : 0,
        latency: totalTests > 0 ? data.latency / totalTests : 0,
        tokenUsage: data.tokenUsage,
        cost: data.cost,
      };
    });
  };

  const modelResults = getModelResults();
  const testCount = evaluation.tests?.length || 0;
  const successRate =
    modelResults.length > 0
      ? modelResults.reduce((sum, model) => sum + model.score, 0) /
        modelResults.length
      : 0;

  // Find key differences between models
  const findKeyDifferences = () => {
    if (modelResults.length < 2) return [];

    const differences = [];

    // Compare latency
    if (Math.abs(modelResults[0].latency - modelResults[1].latency) > 1000) {
      differences.push(
        `${modelResults[0].name} was ${
          modelResults[0].latency < modelResults[1].latency
            ? `${Math.round((modelResults[1].latency / modelResults[0].latency) * 10) / 10}x faster`
            : `${Math.round((modelResults[0].latency / modelResults[1].latency) * 10) / 10}x slower`
        } than ${modelResults[1].name}`,
      );
    }

    // Compare cost
    if (Math.abs(modelResults[0].cost / modelResults[1].cost) > 5) {
      differences.push(
        `${modelResults[0].name} was ${
          modelResults[0].cost > modelResults[1].cost
            ? `${Math.round((modelResults[0].cost / modelResults[1].cost) * 10) / 10}x more expensive`
            : `${Math.round((modelResults[1].cost / modelResults[0].cost) * 10) / 10}x cheaper`
        } than ${modelResults[1].name}`,
      );
    }

    // Compare token usage
    if (
      Math.abs(modelResults[0].tokenUsage - modelResults[1].tokenUsage) > 50
    ) {
      differences.push(
        `${modelResults[0].name} used ${
          modelResults[0].tokenUsage > modelResults[1].tokenUsage
            ? `${Math.round(modelResults[0].tokenUsage - modelResults[1].tokenUsage)} more`
            : `${Math.round(modelResults[1].tokenUsage - modelResults[0].tokenUsage)} fewer`
        } tokens than ${modelResults[1].name}`,
      );
    }

    return differences;
  };

  const keyDifferences = findKeyDifferences();

  // Get failed tests with their error messages
  const getFailedTests = () => {
    if (!evaluation.results || !evaluation.results.results) {
      return [];
    }

    const results = evaluation.results.results as any[];
    return results.filter((result) => !result.success);
  };

  const getTestMetrics = () => {
    if (!evaluation.results || !evaluation.results.results) {
      return { passed: 0, failed: 0, total: 0, passRate: 0 };
    }

    const results = evaluation.results.results as any[];
    const passed = results.filter((result) => result.success).length;
    const failed = results.filter((result) => !result.success).length;
    const total = results.length;
    const passRate = total > 0 ? passed / total : 0;

    return { passed, failed, total, passRate };
  };

  const failedTests = getFailedTests();
  const testMetrics = getTestMetrics();

  const viewDetails = () => {
    navigate(`/evaluations/${evaluation._id}`);
    onClose();
  };

  // Safely extract prompt name
  const promptName = (() => {
    if (!evaluation.prompts || !evaluation.prompts.length)
      return "Unnamed test";
    const prompt = evaluation.prompts[0];
    return typeof prompt === "object" && prompt !== null && "name" in prompt
      ? String(prompt.name)
      : "Unnamed test";
  })();

  // Safely create the model names string
  const modelNamesString = Array.isArray(modelResults)
    ? modelResults.map((m) => String(m.name || "Unknown model")).join(", ")
    : "No models";

  console.log("modelResults", modelResults);
  console.log("evaluation", evaluation);
  console.log("keyDifferences", keyDifferences);
  console.log("successRate", successRate);
  console.log("testCount", testCount);
  console.log("modelNamesString", modelNamesString);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckIcon color="success" />
        <Typography variant="h6">Evaluation Results</Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <Chip
              label={`${Math.round(successRate * 100)}% Success Rate`}
              color={
                successRate > 0.8
                  ? "success"
                  : successRate > 0.5
                    ? "warning"
                    : "error"
              }
              size="small"
              sx={{ mr: 1, fontWeight: "bold" }}
            />
            Test case: {promptName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Models tested: {modelNamesString}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completion: {testCount} tests completed
          </Typography>
        </Box>

        {/* Test Summary Section */}
        <Box sx={{ mb: 3 }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: theme.palette.background.default }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h5" color="success.main">
                    {testMetrics.passed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tests Passed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h5" color="error.main">
                    {testMetrics.failed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tests Failed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h5">
                    {Math.round(testMetrics.passRate * 100)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pass Rate
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Failed Tests Section */}
        {failedTests.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Failed Assertions ({failedTests.length})
              </Typography>
            </Box>

            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: theme.palette.background.default }}
            >
              <List disablePadding>
                {failedTests.map((test, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <Box sx={{ width: "100%" }}>
                      <Alert severity="error" sx={{ mb: 1 }} variant="outlined">
                        <Typography variant="subtitle2">
                          Failure Reason:
                        </Typography>
                        <Typography variant="body2">
                          {test.error ||
                            test.gradingResult?.reason ||
                            "Test failed"}
                        </Typography>
                      </Alert>

                      {test.testCase?.assert && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <AssignmentIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="subtitle2">
                              Expected Behavior:
                            </Typography>
                          </Box>
                          <List disablePadding dense>
                            {Array.isArray(test.testCase.assert) &&
                              test.testCase.assert.map(
                                (assertion: any, assertIdx: number) => (
                                  <ListItem
                                    key={assertIdx}
                                    sx={{ pl: 2, py: 0.5 }}
                                  >
                                    <ListItemText
                                      primary={`${assertion.type}: ${assertion.value}`}
                                    />
                                  </ListItem>
                                ),
                              )}
                          </List>
                        </Box>
                      )}

                      {test.gradingResult?.componentResults && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <AssignmentIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="subtitle2">
                              Assertion Results:
                            </Typography>
                          </Box>
                          <List disablePadding dense>
                            {test.gradingResult.componentResults.map(
                              (component: any, compIdx: number) => (
                                <ListItem key={compIdx} sx={{ pl: 2, py: 0.5 }}>
                                  <ListItemText
                                    primary={
                                      component.assertion?.value ||
                                      "Unknown assertion"
                                    }
                                    secondary={
                                      component.reason || "No reason provided"
                                    }
                                  />
                                </ListItem>
                              ),
                            )}
                          </List>
                        </Box>
                      )}

                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Model Output:
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.background.paper
                                : theme.palette.grey[50],
                            borderRadius: 1,
                            maxHeight: "100px",
                            overflow: "auto",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {String(test.response?.output || "No output")}
                          </Typography>
                        </Paper>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Test Details:
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Model:
                            </Typography>
                            <Typography variant="body2">
                              {test.provider?.id || "Unknown model"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Token Usage:
                            </Typography>
                            <Typography variant="body2">
                              {test.response?.tokenUsage?.total || 0} tokens
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Latency:
                            </Typography>
                            <Typography variant="body2">
                              {test.latencyMs
                                ? `${(test.latencyMs / 1000).toFixed(2)}s`
                                : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Cost:
                            </Typography>
                            <Typography variant="body2">
                              ${test.cost ? test.cost.toFixed(5) : "0.00000"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {index < failedTests.length - 1 && (
                        <Divider sx={{ my: 2 }} />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <CompareIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1">Key Differences</Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: theme.palette.background.default }}
          >
            {keyDifferences.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {keyDifferences.map((diff, index) => (
                  <li key={index}>
                    <Typography variant="body2">{diff}</Typography>
                  </li>
                ))}
              </ul>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No significant differences detected between models
              </Typography>
            )}
          </Paper>
        </Box>

        {modelResults.length >= 2 &&
          modelResults[0].score !== modelResults[1].score && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Different Conclusions Detected
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 1.5, bgcolor: theme.palette.background.default }}
              >
                <Typography variant="body2">
                  Models produced different conclusions from the same input.
                  View details to analyze the differences.
                </Typography>
              </Paper>
            </Box>
          )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button variant="contained" onClick={viewDetails} color="primary">
          View detailed results
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvaluationResultsModal;
